import { frameDisplayUrl } from "../utils/assetMatching";
import type { FilmProject, Scene, SceneChunk, StoryboardFrame } from "../types";
import { enabledSceneChunks, formatRuntime, formatSceneChunking, sceneChunks, sceneStatus } from "../utils/storyboard";

interface InspectorProps {
  project: FilmProject;
  scene: Scene;
  frame: StoryboardFrame;
  onSceneDurationChange: (sceneId: string, durationSeconds: number) => void;
  onResetSceneChunks: (sceneId: string) => void;
  onChunkDurationChange: (sceneId: string, chunkId: string, durationSeconds: number) => void;
  onChunkToggle: (sceneId: string, chunkId: string, field: "enabled" | "locked") => void;
  onSplitChunk: (sceneId: string, chunkId: string) => void;
  onMergeChunk: (sceneId: string, chunkId: string) => void;
  onMoveChunk: (sceneId: string, chunkId: string, direction: -1 | 1) => void;
}

function chunkFramesLabel(chunk: SceneChunk): string {
  return `${chunk.frameIds.length} board${chunk.frameIds.length === 1 ? "" : "s"}`;
}

export function Inspector({
  project,
  scene,
  frame,
  onSceneDurationChange,
  onResetSceneChunks,
  onChunkDurationChange,
  onChunkToggle,
  onSplitChunk,
  onMergeChunk,
  onMoveChunk
}: InspectorProps) {
  const exportIncluded = scene.frames.some((sceneFrame) => !sceneFrame.disabled);
  const readiness = sceneStatus(scene);
  const chunks = sceneChunks(scene);
  const enabledChunks = enabledSceneChunks(scene);

  return (
    <aside className="inspector" aria-label="Scene details">
      <div className="inspector-tabs">
        <button className="active" type="button">Scene</button>
        <button type="button">Shot</button>
        <button type="button">Notes</button>
      </div>
      <img className="inspector-hero" src={frameDisplayUrl(frame)} alt={`${scene.slugline} preview`} />

      <div className="inspector-section">
        <span>{`Scene ${scene.number} of ${project.scenes.length}`}</span>
        <h2>{scene.slugline}</h2>
        <p>{scene.synopsis}</p>
      </div>

      <dl className="scene-timing-grid" aria-label="Scene timing and export readiness">
        <div>
          <dt>Scene Duration</dt>
          <dd>{formatRuntime(scene.durationSeconds)}</dd>
        </div>
        <div>
          <dt>Chunking</dt>
          <dd>{formatSceneChunking(scene)}</dd>
        </div>
        <div>
          <dt>Export</dt>
          <dd>{exportIncluded ? "Included" : "Excluded"}</dd>
        </div>
        <div>
          <dt>Readiness</dt>
          <dd className={`readiness ${scene.readiness}`}>{readiness}</dd>
        </div>
      </dl>

      <section className="chunk-editor" aria-label="Runtime and chunking">
        <header>
          <div>
            <span>Runtime & Chunking</span>
            <strong>{formatRuntime(scene.durationSeconds)} · {enabledChunks.length} active chunk{enabledChunks.length === 1 ? "" : "s"}</strong>
          </div>
          <button className="secondary-action" onClick={() => onResetSceneChunks(scene.id)} type="button">Auto</button>
        </header>

        <label className="duration-field">
          Duration seconds
          <input
            min={1}
            max={900}
            step={1}
            type="number"
            value={scene.durationSeconds}
            onChange={(event) => onSceneDurationChange(scene.id, Number(event.target.value))}
          />
        </label>

        <div className="chunk-list">
          {chunks.map((chunk, index) => (
            <article className={!chunk.enabled ? "disabled" : ""} key={chunk.id}>
              <div className="chunk-row-heading">
                <strong>{chunk.label}</strong>
                <span>{chunkFramesLabel(chunk)}</span>
              </div>
              <label>
                Seconds
                <input
                  disabled={chunk.locked}
                  min={1}
                  max={120}
                  step={1}
                  type="number"
                  value={chunk.durationSeconds}
                  onChange={(event) => onChunkDurationChange(scene.id, chunk.id, Number(event.target.value))}
                />
              </label>
              <div className="chunk-actions">
                <button disabled={index === 0} onClick={() => onMoveChunk(scene.id, chunk.id, -1)} type="button">Up</button>
                <button disabled={index === chunks.length - 1} onClick={() => onMoveChunk(scene.id, chunk.id, 1)} type="button">Down</button>
                <button disabled={chunk.locked || chunk.durationSeconds <= 2} onClick={() => onSplitChunk(scene.id, chunk.id)} type="button">Split</button>
                <button disabled={chunk.locked || index === chunks.length - 1} onClick={() => onMergeChunk(scene.id, chunk.id)} type="button">Merge</button>
                <button onClick={() => onChunkToggle(scene.id, chunk.id, "enabled")} type="button">{chunk.enabled ? "Disable" : "Enable"}</button>
                <button onClick={() => onChunkToggle(scene.id, chunk.id, "locked")} type="button">{chunk.locked ? "Unlock" : "Lock"}</button>
              </div>
            </article>
          ))}
        </div>
      </section>

      <dl className="detail-list">
        <div>
          <dt>Characters</dt>
          <dd>{scene.characters.join(", ")}</dd>
        </div>
        <div>
          <dt>Location</dt>
          <dd>{scene.location}</dd>
        </div>
        <div>
          <dt>Look</dt>
          <dd>{project.stylePreset}</dd>
        </div>
        <div>
          <dt>Color Grade</dt>
          <dd>{project.colorGrade}</dd>
        </div>
      </dl>

      <label className="include-toggle">
        <input type="checkbox" checked={!frame.disabled} readOnly />
        Include selected board in EvoLink package
      </label>
    </aside>
  );
}
